import { FormEvent, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { apiFetch } from "@/lib/api";
import { useToast } from "@/hooks/useToast";
import { PriceRecord } from "@/types/prices";

interface Props {
  location: string;
  utility: string;
  utilityLabel: string;
  unit: string;
  current: PriceRecord | undefined;
  onClose: () => void;
  onSaved: () => void;
}

function nextMonthYM(): string {
  const d = new Date();
  d.setMonth(d.getMonth() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

const SCRAPED_UTILITIES = new Set(["en-el", "gaz"]);

export function ChangePriceModal({ location, utility, utilityLabel, unit, current, onClose, onSaved }: Props) {
  const [value, setValue] = useState("");
  const [estimatedValue, setEstimatedValue] = useState(current?.estimated_value ?? "");
  const [validFrom, setValidFrom] = useState(nextMonthYM);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const { push } = useToast();
  const scraped = SCRAPED_UTILITIES.has(utility);
  const canSave = scraped ? !!estimatedValue && !!validFrom : !!value && !!validFrom;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!canSave) return;
    setLoading(true);
    const [year, month] = validFrom.split("-").map(Number);
    try {
      await apiFetch("/api/utility/prices", {
        method: "POST",
        body: JSON.stringify({
          location, utility, value: scraped ? undefined : value, year, month, notes,
          estimated_value: estimatedValue || undefined,
        }),
      });
      push({ message: `Zapisano cenę: ${utilityLabel} (${location}) od ${validFrom}`, tone: "success" });
      onSaved();
      onClose();
    } catch (err) {
      push({ message: (err as Error).message, tone: "danger" });
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Zmień cenę: ${utilityLabel}`}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={loading}>Anuluj</Button>
          <Button onClick={submit} loading={loading} disabled={!canSave}>
            Zapisz
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className="flex flex-col gap-3">
        {/* Context */}
        <div className="flex items-center justify-between text-xs text-text-muted border border-border rounded px-3 py-2 bg-bg-subtle">
          <span className="capitalize">{location}</span>
          <span className="font-mono">{unit}</span>
          {current && (
            <span>Obecna: <strong>{current.value}</strong> od {current["year-month"]}</span>
          )}
        </div>

        {!scraped && <Input
          id="cp-value"
          label={`Nowa cena (${unit})`}
          inputMode="decimal"
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={current?.value ?? "0.00"}
        />}

        <Input
          id="cp-estimated-value"
          label="Kwota szacunkowa (PLN)"
          inputMode="decimal"
          value={estimatedValue}
          onChange={(e) => setEstimatedValue(e.target.value)}
          placeholder={current?.estimated_value ?? "0.00"}
          hint="Naliczana, gdy brak rzeczywistego odczytu w dniu zamknięcia zbierania"
        />

        {/* month picker — native <input type="month"> gives YYYY-MM */}
        <div className="flex flex-col gap-1">
          <label htmlFor="cp-from" className="text-xs text-text-muted font-medium">
            Obowiązuje od
          </label>
          <input
            id="cp-from"
            type="month"
            value={validFrom}
            onChange={(e) => setValidFrom(e.target.value)}
            className="h-9 rounded border border-border bg-bg px-3 text-sm text-text focus:outline-none focus:ring-1 focus:ring-accent"
          />
          <p className="text-xs text-text-muted">
            Cena będzie stosowana od tego miesiąca aż do wprowadzenia nowszej.
          </p>
        </div>

        <Input
          id="cp-notes"
          label="Notatka (opcjonalna)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="np. nowa taryfa miejska"
        />
      </form>
    </Modal>
  );
}
